import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { colors, hairline, radii, spacing } from '../../theme';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * The way in to the alarm, on a booking that is happening now.
 *
 * ---------------------------------------------------------------------------
 * THIS ONE OPENS THE SCREEN - IT DOES NOT RAISE THE ALARM
 * ---------------------------------------------------------------------------
 * One tap here opens `SosScreen`, where a press-and-hold sends it. A row on a
 * scrolling page cannot be the trigger: it would fire from a thumb resting on
 * the list, and an alarm that cries wolf is an alarm ops learn to discount.
 *
 * The hold lives on a screen of its own where nothing else can be pressed by
 * accident, and getting there is one tap from the booking someone is worried
 * about.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS QUIET
 * ---------------------------------------------------------------------------
 * Outlined in the danger ink rather than a solid red bar. This sits on the
 * screen of a visit that is going perfectly well, which is nearly all of them,
 * and a red slab across a normal booking makes the app feel dangerous to use.
 * It has to be findable in two seconds without being the loudest thing on the
 * page for the other fifty-nine minutes.
 */

function SosButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Emergency help. Opens the safety screen."
      accessibilityHint="You will be asked to press and hold before an alert is sent"
      onPress={onPress}
      style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}
    >
      <View style={styles.mark}>
        <Icon name="siren" size={20} color={colors.danger} />
      </View>

      <View style={styles.body}>
        <Text variant="subtitle" color={colors.danger}>
          Emergency help
        </Text>
        <Text variant="caption" color={colors.inkMuted}>
          Alerts our safety team with your location
        </Text>
      </View>

      <Icon name="chevronRight" size={16} color={colors.danger} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
    borderColor: colors.danger,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.dangerSurface,
  },
  body: {
    flex: 1,
    gap: 2,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default SosButton;
